import React, { useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../utils/cn';

/**
 * Accessible tab list with optional animated indicator.
 *
 * @param {Object} props
 * @param {{ id: string, label: React.ReactNode, icon?: React.ComponentType, badge?: React.ReactNode }[]} props.items
 * @param {string} props.value - active tab id
 * @param {(id: string) => void} props.onChange
 * @param {string} [props.layoutId] - framer layoutId for shared indicator
 * @param {'pills'|'underline'} [props.variant]
 */
export function Tabs({ items, value, onChange, layoutId = 'ui-tabs', variant = 'pills', className, ariaLabel }) {
  const tabRefs = useRef({});

  const focusTab = useCallback((id) => {
    tabRefs.current[id]?.focus();
  }, []);

  const handleKeyDown = (e, index) => {
    const ids = items.map((item) => item.id);
    let nextIndex = index;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      nextIndex = (index + 1) % ids.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      nextIndex = (index - 1 + ids.length) % ids.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIndex = ids.length - 1;
    } else {
      return;
    }

    const nextId = ids[nextIndex];
    onChange(nextId);
    focusTab(nextId);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex flex-wrap justify-center gap-2',
        variant === 'underline' && 'border-b border-gray-200 gap-0',
        className
      )}
    >
      {items.map((item, index) => {
        const isActive = value === item.id;
        const Icon = item.icon;

        return (
          <button
            key={item.id}
            ref={(el) => {
              tabRefs.current[item.id] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${item.id}`}
            aria-selected={isActive}
            aria-controls={`panel-${item.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(item.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              'relative flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              variant === 'pills' && 'rounded-full',
              variant === 'underline' && 'rounded-none border-b-2 -mb-px',
              isActive && variant === 'pills' && 'text-white',
              isActive && variant === 'underline' && 'border-primary-600 text-primary-700',
              !isActive && variant === 'pills' && 'text-gray-600 bg-white border border-gray-200 hover:border-primary-200 hover:text-primary-700',
              !isActive && variant === 'underline' && 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            {isActive && variant === 'pills' && (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-0 bg-gradient-to-r from-primary-600 to-accent-600 rounded-full"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative flex items-center gap-2">
              {Icon && <Icon className="w-4 h-4" aria-hidden />}
              {item.label}
              {item.badge != null && (
                <span className={cn('text-xs', isActive && variant === 'pills' ? 'text-white/80' : 'text-gray-400')}>
                  {item.badge}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, activeId, children, className }) {
  const isActive = id === activeId;

  if (!isActive) return null;

  return (
    <div
      role="tabpanel"
      id={`panel-${id}`}
      aria-labelledby={`tab-${id}`}
      tabIndex={0}
      className={cn('outline-none', className)}
    >
      {children}
    </div>
  );
}
